/**
 * Module SessionManager - Quản lý phiên làm việc & Lịch sử gần đây (Recent Activities)
 * Chịu trách nhiệm theo dõi:
 * - Người dùng đang đọc truyện gì, chương nào, tiến độ bao nhiêu %, TTS thế nào.
 * - Người dùng đang xem video YouTube nào, giây thứ bao nhiêu.
 * Cung cấp dữ liệu để Homepage hiển thị "Continue Card" và "Recent Activities" chuẩn xác.
 */
import { WorkspaceStorage } from './workspaceStorage.js';
import { appRegistry } from './appRegistry.js';
import { workspaceDB, IDB_STORES } from './workspaceIndexedDB.js';

const STORAGE_KEYS = {
  ACTIVE_SESSION: 'active_session',
  RECENT_LIST: 'recent_list',
  FAVORITES_LIST: 'favorites_list'
};

const MAX_RECENT_ITEMS = 20;

export class SessionManager {
  constructor() {
    this.lastVideoSaveTimestamp = 0;
    /** @type {Array<Object>} Bộ nhớ đệm danh sách 20 tài liệu gần đây để truy xuất tức thì */
    this.inMemoryRecent = [];
    this.isInitialized = false;

    // Khởi tạo nạp dữ liệu từ IndexedDB ngay khi tạo instance
    this.initPromise = this.init();
  }

  /**
   * Khởi tạo nạp dữ liệu từ IndexedDB vào bộ nhớ đệm
   * Tự động migrate dữ liệu cũ từ LocalStorage sang IndexedDB nếu có
   */
  async init() {
    try {
      // 1. Đọc dữ liệu từ IndexedDB trước
      const idbList = await workspaceDB.getRecentSorted(MAX_RECENT_ITEMS);
      if (Array.isArray(idbList) && idbList.length > 0) {
        this.inMemoryRecent = idbList.slice(0, MAX_RECENT_ITEMS);
        // Đồng bộ đệm sang LocalStorage làm fast fallback
        WorkspaceStorage.set(STORAGE_KEYS.RECENT_LIST, this.inMemoryRecent);
        this.isInitialized = true;
        return;
      }

      // 2. Nếu IndexedDB chưa có, kiểm tra LocalStorage để migrate sang IndexedDB
      const localList = WorkspaceStorage.get(STORAGE_KEYS.RECENT_LIST, []);
      if (Array.isArray(localList) && localList.length > 0) {
        this.inMemoryRecent = localList.slice(0, MAX_RECENT_ITEMS);
        console.log(`[SessionManager] Đang chuyển ${this.inMemoryRecent.length} tài liệu từ LocalStorage sang IndexedDB...`);
        await workspaceDB.putMany(IDB_STORES.RECENT, this.inMemoryRecent);
        await workspaceDB.trimRecent(MAX_RECENT_ITEMS);
      }
      this.isInitialized = true;
    } catch (err) {
      console.warn('[SessionManager] Lỗi khởi tạo IndexedDB:', err);
      // Fallback về LocalStorage an toàn
      this.inMemoryRecent = WorkspaceStorage.get(STORAGE_KEYS.RECENT_LIST, []) || [];
      this.isInitialized = true;
    }
  }

  /**
   * Cập nhật phiên làm việc của Chế độ Đọc Truyện (Sheets, Email, Zalo)
   * @param {Object} params
   * @param {'sheets' | 'email' | 'zalo'} params.appId Mã ứng dụng
   * @param {string} params.source Nguồn crawl (ví dụ 's1')
   * @param {string} params.storyId Mã định danh truyện
   * @param {string} params.chapterId Mã định danh chương
   * @param {string} params.truyenTitle Tiêu đề truyện
   * @param {string} params.chapterTitle Tiêu đề chương
   * @param {number} [params.chapterNumber=0] Thứ tự chương đang đọc (bắt đầu từ 1)
   * @param {number} [params.totalChapters=0] Tổng số chương của truyện
   * @param {number} [params.sentenceIndex=0] Dòng/câu đang đọc
   * @param {number} [params.totalSentences=1] Tổng số dòng/câu trong chương
   * @param {Object} [params.tts] Trạng thái âm thanh TTS
   * @returns {Object|null} Session đã tạo
   */
  updateReaderSession(params) {
    if (!params || !params.storyId || !params.chapterId) return null;

    const appId = params.appId || 'sheets';
    const appInfo = appRegistry.getAppById(appId);
    const truyenTitle = (params.truyenTitle || 'Tài liệu đọc').trim();
    const chapterTitle = (params.chapterTitle || 'Chương hiện tại').trim();

    // Vị trí câu dùng để khôi phục TTS; tiến độ truyện tính theo chương.
    const total = Math.max(1, Number(params.totalSentences) || 1);
    const current = Math.max(0, Number(params.sentenceIndex) || 0);
    const chapterNumber = Number.isFinite(params.chapterNumber) ? Math.max(0, Math.floor(params.chapterNumber)) : 0;
    const totalChapters = Number.isFinite(params.totalChapters) ? Math.max(0, Math.floor(params.totalChapters)) : 0;
    const progressPercent = chapterNumber > 0 && totalChapters > 0
      ? Math.min(100, Math.round((chapterNumber / totalChapters) * 1000) / 10)
      : null;

    const disguiseTitle = `Báo cáo_${truyenTitle.replace(/\s+/g, '_')}`;
    const subtitle = progressPercent === null
      ? `${chapterTitle} · Chưa có tổng số chương`
      : `${chapterTitle} · ${chapterNumber}/${totalChapters} chương · ${progressPercent}%`;

    const session = {
      sessionId: `session_reader_${params.storyId}`,
      appId: appId,
      type: 'reader',
      contentId: params.storyId,
      title: disguiseTitle,
      rawTitle: truyenTitle,
      subtitle: subtitle,
      progress: progressPercent,
      progressBasis: 'chapters',
      progressText: subtitle,
      thumbnail: '',
      startedAt: Date.now(),
      lastActiveAt: Date.now(),
      position: {
        source: params.source || 's1',
        storyId: params.storyId,
        chapterId: params.chapterId,
        chapterNumber,
        totalChapters,
        sentenceIndex: current,
        totalSentences: total,
        tts: params.tts || { enabled: false, speed: 1.0 }
      }
    };

    // 1. Lưu phiên hoạt động gần nhất vào storage
    WorkspaceStorage.set(STORAGE_KEYS.ACTIVE_SESSION, session);

    // 2. Thêm hoặc cập nhật vào danh sách Recent Activities
    this.touchRecent({
      id: session.sessionId,
      appId: appId,
      appName: appInfo?.name || 'Google Sheets',
      contentId: params.storyId,
      title: `${truyenTitle} (${chapterTitle})`,
      disguiseTitle: `${disguiseTitle}.${appInfo?.disguiseDocType || 'xlsx'}`,
      subtitle: subtitle,
      route: `#/${appId}?source=${encodeURIComponent(params.source || 's1')}&story=${encodeURIComponent(params.storyId)}&chap=${encodeURIComponent(params.chapterId)}`,
      progress: progressPercent,
      progressBasis: 'chapters',
      chapterNumber,
      totalChapters,
      lastActiveAt: Date.now(),
      iconSvg: appInfo?.iconSvg || ''
    });

    return session;
  }

  /**
   * Cập nhật phiên làm việc của Chế độ Video (Canva hoặc CapCut)
   * Có throttle 3 giây để tránh ghi đĩa liên tục khi video đang phát
   * @param {Object} params
   * @param {'canva' | 'capcut'} params.appId Mã ứng dụng
   * @param {string} params.videoId Mã video YouTube 11 ký tự
   * @param {string} params.title Tiêu đề video
   * @param {string} [params.author] Kênh tác giả
   * @param {number} [params.currentTime=0] Giây hiện tại
   * @param {number} [params.duration=0] Tổng thời lượng video
   * @param {string} [params.thumbnail] Ảnh thumbnail
   * @param {boolean} [force=false] Bắt buộc ghi không qua throttle
   * @returns {Object|null}
   */
  updateVideoSession(params, force = false) {
    if (!params || !params.videoId) return null;

    const now = Date.now();
    // Throttle tối thiểu 3 giây giữa các lần ghi để bảo vệ hiệu năng
    if (!force && now - this.lastVideoSaveTimestamp < 3000) {
      return null;
    }
    this.lastVideoSaveTimestamp = now;

    const appId = params.appId || 'canva';
    const appInfo = appRegistry.getAppById(appId);
    const videoId = params.videoId.trim();
    const videoTitle = (params.title || 'Video tư liệu').trim();
    const curTime = Math.max(0, Math.round(Number(params.currentTime) || 0));
    const dur = Math.max(0, Math.round(Number(params.duration) || 0));

    const progressPercent = dur > 0 ? Math.min(100, Math.max(0, Math.round((curTime / dur) * 100))) : 0;

    const formatTime = (secs) => {
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const timeFormatted = dur > 0 ? `${formatTime(curTime)} / ${formatTime(dur)}` : formatTime(curTime);
    let disguiseTitle = `Video_Tu_Lieu_${videoId.substring(0, 6).toUpperCase()}`;
    let subtitleText = `${timeFormatted} • ${params.author || 'Tư liệu'}`;
    let targetRoute = `#/${appId}?v=${encodeURIComponent(videoId)}&t=${curTime}`;

    if (appId === 'figma') {
      disguiseTitle = `UI_Prototype_${videoId.substring(0, 6).toUpperCase()}`;
      subtitleText = `${params.author || 'UI Screen'} • Shorts 9:16`;
      targetRoute = `#/figma?v=${encodeURIComponent(videoId)}`;
    } else if (appId === 'emulator') {
      disguiseTitle = `App_Testing_${videoId.substring(0, 6).toUpperCase()}`;
      subtitleText = `${params.author || 'Test Case'} • Shorts 9:16`;
      targetRoute = `#/emulator?v=${encodeURIComponent(videoId)}`;
    }

    const session = {
      sessionId: `session_video_${videoId}`,
      appId: appId,
      type: 'video',
      contentId: videoId,
      title: disguiseTitle,
      rawTitle: videoTitle,
      subtitle: subtitleText,
      progress: progressPercent || 100,
      progressText: timeFormatted,
      thumbnail: params.thumbnail || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
      startedAt: Date.now(),
      lastActiveAt: Date.now(),
      position: {
        videoId: videoId,
        currentTime: curTime,
        duration: dur,
        author: params.author || ''
      }
    };

    // 1. Lưu phiên hoạt động gần nhất vào storage
    WorkspaceStorage.set(STORAGE_KEYS.ACTIVE_SESSION, session);

    // 2. Thêm vào danh sách Recent
    this.touchRecent({
      id: session.sessionId,
      type: 'video',
      appId: appId,
      appName: appInfo?.name || (appId === 'figma' ? 'Figma Prototype' : (appId === 'emulator' ? 'Android Studio' : 'Canva Design')),
      contentId: videoId,
      videoId,
      title: videoTitle,
      rawTitle: videoTitle,
      duration: params.duration || 0,
      author: params.author || '',
      disguiseTitle: `${disguiseTitle}.${appInfo?.disguiseDocType || (appId === 'figma' ? 'fig' : (appId === 'emulator' ? 'apk' : 'mp4'))}`,
      subtitle: subtitleText,
      route: targetRoute,
      progress: progressPercent || 100,
      lastActiveAt: Date.now(),
      thumbnail: session.thumbnail,
      iconSvg: appInfo?.iconSvg || ''
    });

    return session;
  }

  /**
   * Lấy phiên làm việc gần nhất để phục vụ hiển thị Continue Card trên Homepage
   * @returns {Object|null}
   */
  getLastActiveSession() {
    return WorkspaceStorage.get(STORAGE_KEYS.ACTIVE_SESSION, null);
  }

  /**
   * Ghi nhận một mục vào danh sách Recent Activities
   * Lưu trữ đồng bộ 3 tầng:
   * 1. In-Memory: Trả về tức thì cho UI không giật lag
   * 2. LocalStorage: Bộ đệm dự phòng nhanh
   * 3. IndexedDB: Lưu trữ chuẩn bền vững, dung lượng lớn, tự động cắt 20 mục gần nhất
   * @param {Object} item Thông tin tài liệu hoặc video
   */
  touchRecent(item) {
    if (!item || !item.id) return;

    try {
      // 1. Cập nhật bộ nhớ đệm In-Memory
      const currentList = Array.isArray(this.inMemoryRecent) && this.inMemoryRecent.length > 0
        ? this.inMemoryRecent
        : WorkspaceStorage.get(STORAGE_KEYS.RECENT_LIST, []);

      // Loại bỏ mục cũ nếu đã tồn tại để đưa lên đầu danh sách
      const filtered = currentList.filter(r => r.id !== item.id && r.contentId !== item.contentId);
      filtered.unshift({
        ...item,
        lastActiveAt: item.lastActiveAt || Date.now()
      });

      // Cắt gọn danh sách đúng 20 mục gần nhất
      this.inMemoryRecent = filtered.slice(0, MAX_RECENT_ITEMS);

      // 2. Lưu vào LocalStorage làm fast fallback
      WorkspaceStorage.set(STORAGE_KEYS.RECENT_LIST, this.inMemoryRecent);

      // 3. Lưu bất đồng bộ bền vững vào IndexedDB (không block luồng chính)
      workspaceDB.put(IDB_STORES.RECENT, item).then(() => {
        // Tự động xóa các tài liệu cũ quá 20 mục khỏi IndexedDB
        return workspaceDB.trimRecent(MAX_RECENT_ITEMS);
      }).catch(err => {
        console.warn('[SessionManager] Cảnh báo lưu IndexedDB:', err);
      });
    } catch (err) {
      console.warn('[SessionManager] Lỗi cập nhật Recent:', err);
    }
  }

  /**
   * Lấy danh sách các tài liệu / hoạt động mở gần đây (Đồng bộ từ cache, mặc định 20 mục)
   * @param {number} [limit=20] Số lượng mục tối đa
   * @returns {Array<Object>}
   */
  getRecentItems(limit = 20) {
    if (Array.isArray(this.inMemoryRecent) && this.inMemoryRecent.length > 0) {
      return this.inMemoryRecent.slice(0, limit);
    }
    const list = WorkspaceStorage.get(STORAGE_KEYS.RECENT_LIST, []);
    return Array.isArray(list) ? list.slice(0, limit) : [];
  }

  /**
   * Lấy danh sách 20 tài liệu gần đây bất đồng bộ trực tiếp từ IndexedDB
   * @param {number} [limit=20] 
   * @returns {Promise<Array<Object>>}
   */
  async getRecentItemsAsync(limit = 20) {
    try {
      const items = await workspaceDB.getRecentSorted(limit);
      if (Array.isArray(items) && items.length > 0) {
        this.inMemoryRecent = items;
        WorkspaceStorage.set(STORAGE_KEYS.RECENT_LIST, items);
        return items;
      }
    } catch (err) {
      console.warn('[SessionManager] Lỗi đọc Recent từ IndexedDB:', err);
    }
    return this.getRecentItems(limit);
  }

  /**
   * Xóa một mục khỏi danh sách gần đây ở cả In-Memory, LocalStorage và IndexedDB
   * @param {string} id Mã định danh tài liệu
   */
  async removeRecentItem(id) {
    if (!id) return;

    // 1. Xóa khỏi In-Memory
    this.inMemoryRecent = this.inMemoryRecent.filter(r => r.id !== id);

    // 2. Xóa khỏi LocalStorage
    const list = WorkspaceStorage.get(STORAGE_KEYS.RECENT_LIST, []);
    const updated = list.filter(r => r.id !== id);
    WorkspaceStorage.set(STORAGE_KEYS.RECENT_LIST, updated);

    // 3. Xóa khỏi IndexedDB
    try {
      await workspaceDB.delete(IDB_STORES.RECENT, id);
    } catch (err) {
      console.warn('[SessionManager] Lỗi xóa mục khỏi IndexedDB:', err);
    }
  }

  /**
   * Xóa phiên hoạt động đang mở
   */
  clearActiveSession() {
    WorkspaceStorage.remove(STORAGE_KEYS.ACTIVE_SESSION);
  }
}

// Xuất ra một instance duy nhất (Singleton Pattern) để dùng chung
export const sessionManager = new SessionManager();
